import { useDispatch, useSelector } from "react-redux";
import { State } from "store";
import { useTranslation } from "utilities/i18n";
import React, { useContext } from "react";
import { PolyIcon } from "elements";
import { startFileDownload } from "utilities/fileUtils";
import { useExportDeviceInventoryMutation } from "generated/graphql";
import { startExport, stopExport } from "store/deviceExport";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { useNotification, DISMISS } from "collections";
import { Guard } from "TsRoleGuards";
import { ProtectedButton } from "TsRoleGuards";

export function useDeviceExport() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [addNotification] = useNotification();
  const { tenant } = useContext(SelectedTenantContext);

  const tenantId = tenant?.id || "";

  const { exporting } = useSelector(
    (state: State) => state.exportDeviceInventory[tenantId] ?? {}
  );

  const [, requestInventoryData] = useExportDeviceInventoryMutation();

  async function exportInventoryData() {
    dispatch(
      startExport({
        tenantId
      })
    );

    const { data } = await requestInventoryData({ tenantId });

    const recipientEmail = data?.exportDeviceInventory.recipientEmail;
    const downloadLink = data?.exportDeviceInventory.link;

    if (recipientEmail) {
      sendNotification(recipientEmail);
    } else if (downloadLink) {
      exportFile(downloadLink as string);
    }
  }

  function exportFile(exportUrl: string): void {
    startFileDownload(exportUrl);
    sendNotification();
  }

  function sendNotification(email?: string): void {
    email
      ? addNotification({
          id: "device-inventory-export",
          type: "info",
          message: t("EXPORT_DEVICE_INVENTORY_EMAIL", {
            email: email
          }),
          dismiss: DISMISS.USER_ONLY
        })
      : addNotification({
          id: "device-inventory-export",
          type: "success",
          message: t("EXPORT_DEVICE_INVENTORY_SUCCESS_MESSAGE"),
          dismiss: DISMISS.DEFAULT
        });
    dispatch(
      stopExport({
        tenantId
      })
    );
  }

  return {
    exportInventoryData,
    exporting
  };
}

export const DeviceInventoryExport: React.FC<{}> = () => {
  const { exportInventoryData, exporting } = useDeviceExport();
  const { t } = useTranslation();

  return (
    <Guard requirement={"export:device"}>
      <>
        {!exporting ? (
          <>
            <ProtectedButton
              className="btn btn-secondary mr-1"
              onClick={exportInventoryData}
              requirement="export:device"
            >
              {t("EXPORT")}
            </ProtectedButton>
          </>
        ) : (
          <button className="btn mr-1" disabled>
            <PolyIcon icon="refresh" className="refresh spin mr-5" />
            {t("EXPORTING")}
          </button>
        )}
      </>
    </Guard>
  );
};
